import React from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS } from '../../theme';
import { Skeleton, SkeletonAvatar } from '../common/Skeleton';

/**
 * Loading placeholder for /structure/[id], shaped like the loaded page
 * (AddressHeader, the readiness hero, then a few activity rows) instead of
 * a centered spinner blanking the whole screen. Full-screen only — this
 * route never renders inside the desktop DockedPanel (that's a different
 * component, StructureDetailSheet, which has its own partial skeleton).
 */
export function StructureDetailSkeleton() {
  const { colors } = useTheme();

  return (
    <View style={styles.container} testID="structure-detail-skeleton">
      {/* Address header: title line + a shorter subtitle line */}
      <Skeleton width="70%" height={24} style={styles.title} />
      <Skeleton width="45%" height={14} style={styles.subtitle} />

      {/* Readiness hero block */}
      <View
        style={[
          styles.hero,
          { backgroundColor: colors.surfaceElevated, borderColor: colors.border },
        ]}>
        <View style={styles.heroRow}>
          <SkeletonAvatar size={48} />
          <View style={styles.heroText}>
            <Skeleton width="50%" height={16} />
            <Skeleton width="30%" height={12} style={{ marginTop: SPACING.xs }} />
          </View>
        </View>
        <Skeleton height={10} borderRadius={BORDER_RADIUS.full} style={{ marginTop: SPACING.md }} />
      </View>

      {/* A few activity/list rows */}
      {[0, 1, 2].map((i) => (
        <View
          key={i}
          style={[styles.row, { borderColor: colors.border }]}
          testID={`structure-detail-skeleton-row-${i}`}>
          <Skeleton width={40} height={40} borderRadius={BORDER_RADIUS.md} />
          <View style={styles.rowText}>
            <Skeleton width="80%" height={13} />
            <Skeleton width="40%" height={11} style={{ marginTop: SPACING.xs }} />
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: SPACING.md,
  },
  title: {
    marginBottom: SPACING.xs,
  },
  subtitle: {
    marginBottom: SPACING.sm,
  },
  hero: {
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    padding: SPACING.md,
  },
  heroRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  heroText: {
    marginLeft: SPACING.smd,
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowText: {
    marginLeft: SPACING.smd,
    flex: 1,
  },
});
